import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/verticals";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CurrencyEur, CalendarCheck, Star, TrendUp, ChatCircle, BellSimple, UsersThree } from "@phosphor-icons/react";

const STATUS_OPTS = ["pending", "confirmed", "completed", "cancelled"];

const ProviderDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(undefined);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [staffFilter, setStaffFilter] = useState("all");

  const load = useCallback(async () => {
    const [p, s, b, st, w] = await Promise.allSettled([
      api.get("/providers/me"), api.get("/provider/stats"), api.get("/bookings"),
      api.get("/staff"), api.get("/waitlist"),
    ]);
    setProfile(p.status === "fulfilled" ? p.value.data : null);
    if (s.status === "fulfilled") setStats(s.value.data);
    if (b.status === "fulfilled") setBookings(b.value.data);
    if (st.status === "fulfilled") setStaff(st.value.data);
    if (w.status === "fulfilled") setWaitlist(w.value.data);
  }, []);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (!loading && user && user.role !== "professionista") { navigate("/dashboard"); return; }
    if (user) load();
  }, [user, loading, navigate, load]);

  useEffect(() => {
    if (profile === null) navigate("/pro/onboarding");
  }, [profile, navigate]);

  const setStatus = async (id, status) => {
    await api.patch(`/bookings/${id}/status`, { status });
    toast.success("Stato aggiornato");
    load();
  };

  const closeWaitlist = async (id) => {
    await api.delete(`/waitlist/${id}`);
    toast.success("Voce lista d'attesa gestita");
    load();
  };

  if (!user || !profile) return null;

  const cards = [
    { label: "Incassi", value: eur(stats?.earnings || 0), Icon: CurrencyEur, cls: "text-emerald-600" },
    { label: "Prenotazioni", value: stats?.total_bookings || 0, Icon: CalendarCheck, cls: "text-primary" },
    { label: "In arrivo", value: stats?.upcoming || 0, Icon: TrendUp, cls: "text-blue-600" },
    { label: "Valutazione", value: stats?.rating || "—", Icon: Star, cls: "text-amber-500" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">{profile.business_name}</h1>
          <p className="text-muted-foreground">Dashboard professionista</p>
        </div>
        <Button variant="outline" className="rounded-full" data-testid="edit-profile-btn" onClick={() => navigate("/pro/onboarding")}>
          Modifica profilo & servizi
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="pro-stats">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-2xl p-5">
            <c.Icon size={22} className={c.cls} weight="duotone" />
            <div className="text-2xl font-bold font-heading mt-3">{c.value}</div>
            <div className="text-sm text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 mb-4 flex-wrap gap-3">
        <h2 className="font-heading text-xl font-bold">Appuntamenti</h2>
        {staff.length > 0 && (
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger className="w-52 rounded-full" data-testid="staff-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli operatori</SelectItem>
              {staff.map((m) => <SelectItem key={m.staff_id} value={m.staff_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="space-y-3" data-testid="pro-bookings">
        {bookings.filter((b) => staffFilter === "all" || b.staff_id === staffFilter).length === 0 && <p className="text-muted-foreground text-sm py-8 text-center border border-dashed rounded-2xl">Nessun appuntamento.</p>}
        {bookings.filter((b) => staffFilter === "all" || b.staff_id === staffFilter).map((b) => (
          <div key={b.booking_id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`pro-booking-${b.booking_id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{b.service_name}</span>
                {b.payment_status === "paid" && <Badge variant="outline" className="text-emerald-600 border-emerald-200">Pagato</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{b.customer_name} · {b.date} {b.start_time}{b.staff_name ? ` · ${b.staff_name}` : ""}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Guadagno netto: <b>{eur(b.provider_payout)}</b> (commissione {eur(b.platform_fee)})</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="ghost" data-testid={`pro-chat-${b.booking_id}`} onClick={() => navigate(`/booking/${b.booking_id}/chat`)}><ChatCircle /></Button>
              <Select value={b.status} onValueChange={(v) => setStatus(b.booking_id, v)}>
                <SelectTrigger className="w-36 rounded-full" data-testid={`status-select-${b.booking_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-xl font-bold mt-8 mb-4 flex items-center gap-2"><BellSimple size={20} weight="duotone" /> Lista d'attesa</h2>
      <div className="space-y-2" data-testid="waitlist-section">
        {waitlist.filter((w) => w.status === "waiting").length === 0 && <p className="text-muted-foreground text-sm py-6 text-center border border-dashed rounded-2xl">Nessun cliente in lista d'attesa.</p>}
        {waitlist.filter((w) => w.status === "waiting").map((w) => (
          <div key={w.waitlist_id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`waitlist-${w.waitlist_id}`}>
            <div>
              <div className="font-medium">{w.customer_name} <span className="text-muted-foreground font-normal">· {w.service_name}</span></div>
              <p className="text-sm text-muted-foreground">Data desiderata: {w.date}{w.customer_phone ? ` · ${w.customer_phone}` : ""}</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-full" data-testid={`wl-close-${w.waitlist_id}`} onClick={() => closeWaitlist(w.waitlist_id)}>Segna gestito</Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProviderDashboard;
